/* 商品页交互，三组：画廊切帧、规格与配件选择、加购与立即购买。
   只在商品页加载一次（layout/theme.liquid 按 request.page_type == 'product' 判断）。

   点击统一在 document 上委托，不加在各自按钮上：主题编辑器重渲染 section 后
   不需要重新绑定。需要跨事件保留的状态挂在 root 元素上，用 WeakMap 存取，
   shopify:section:unload 时删条目，编辑器里反复重渲染不会攒下旧 DOM 的引用。

   加购走 Dawn 的契约：把购物车浮层自己声明的 section 一并提交，成功后交给它的
   renderContents（它内部会开抽屉），并 publish cartUpdate 让其他订阅者跟上；
   没有浮层时落到 /cart，与 assets/product-form.js 的兜底一致。失败不弹 alert，
   错误写进按钮下方的 .starole-product__error。 */

(function () {
  const productStates = new WeakMap();
  const imagePreloadCache = new Map();

  // 非活动的媒体帧是 display:none，帧里的图 loading="lazy"，浏览器不会去加载它们，
  // 切帧那一帧只有 CSS 占位（aspect-ratio + 灰底）。空闲时按低优先级先把它们抓进缓存，
  // 切到某帧时再把该帧提到高优先级。
  const warmImage = (image, priority = 'low') => {
    if (!image) return;
    const warmedAt = image.dataset.staroleImageWarmed;
    if (warmedAt === 'high' || warmedAt === priority) return;
    image.dataset.staroleImageWarmed = priority;
    image.loading = priority === 'high' ? 'eager' : 'lazy';
    image.decoding = 'async';
    image.fetchPriority = priority;

    const src = image.getAttribute('src');
    const srcset = image.getAttribute('srcset');
    const sizes = image.getAttribute('sizes') || '';
    const cacheKey = `${srcset || ''}|${sizes}|${src || ''}`;
    if (!cacheKey) return;
    if (imagePreloadCache.has(cacheKey)) {
      if (priority === 'high') imagePreloadCache.get(cacheKey).fetchPriority = 'high';
      return;
    }

    const preloadImage = new Image();
    preloadImage.decoding = 'async';
    preloadImage.fetchPriority = priority;
    if (srcset) {
      preloadImage.srcset = srcset;
      preloadImage.sizes = sizes;
    }
    if (src) preloadImage.src = src;
    imagePreloadCache.set(cacheKey, preloadImage);
    preloadImage.onload = () => imagePreloadCache.delete(cacheKey);
    preloadImage.onerror = () => imagePreloadCache.delete(cacheKey);
  };

  const warmImageSet = (images, priority = 'low') => [...images].forEach((image) => warmImage(image, priority));

  // 返回拆除函数：section 被换掉时 IntersectionObserver 还攥着旧 root
  // （它强引用观察目标，而目标脱离文档后回调只报一次「不相交」，不会走到 disconnect），
  // 编辑器里反复重渲染就会攒下旧 DOM。由 teardownProduct 调用。
  const scheduleImageWarmup = (scope, selector) => {
    const images = [...scope.querySelectorAll(selector)];
    if (!images.length) return null;

    let started = false;
    let observer = null;
    const isSmallViewport = window.matchMedia?.('(max-width: 760px)').matches;
    const start = () => {
      if (started) return;
      started = true;
      warmImageSet(images.slice(0, isSmallViewport ? 4 : images.length));
    };
    const schedule = () => {
      if (isSmallViewport) {
        start();
        return;
      }
      if ('requestIdleCallback' in window) window.requestIdleCallback(start, { timeout: 900 });
      else window.setTimeout(start, 180);
    };

    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        observer.disconnect();
        schedule();
      }, { rootMargin: '800px 0px', threshold: 0.01 });
      observer.observe(scope);
    } else {
      schedule();
    }

    return () => {
      started = true;
      if (!observer) return;
      observer.disconnect();
      observer = null;
    };
  };

  const formatMoney = (cents, currency, locale) =>
    new Intl.NumberFormat(locale, { style: 'currency', currency }).format(cents / 100);

  const findCartSurface = () =>
    document.querySelector('cart-notification') || document.querySelector('cart-drawer');

  const showMedia = (state, mediaId) => {
    const index = state.thumbnails.findIndex((thumbnail) => thumbnail.dataset.mediaId === String(mediaId));
    if (index < 0) return;

    warmImageSet(state.mediaItems[index].querySelectorAll('img'), 'high');

    state.mediaItems.forEach((media, mediaIndex) => {
      const active = mediaIndex === index;
      media.classList.toggle('is-active', active);
      media.querySelectorAll('video').forEach((video) => {
        if (!active) video.pause();
      });
    });
    state.thumbnails.forEach((thumbnail, thumbnailIndex) => {
      thumbnail.classList.toggle('is-active', thumbnailIndex === index);
    });
  };

  // 展示价是「规格价 + 配件价」，服务端首屏只渲染规格价，所以配件一动就要重算
  const updateDisplayedPrice = (state) => {
    const total = state.baseCents + state.accessoryCents;
    if (state.priceEl) {
      state.priceEl.textContent = `${formatMoney(total, state.currency, state.locale)} ${state.currency}`;
    }
    if (!state.compareEl) return;

    const hasComparePrice = state.compareCents > state.baseCents;
    state.compareEl.hidden = !hasComparePrice;
    state.compareEl.textContent = hasComparePrice
      ? formatMoney(state.compareCents + state.accessoryCents, state.currency, state.locale)
      : '';
  };

  const selectVariant = (state, option) => {
    state.tierOptions.forEach((item) => item.classList.toggle('is-active', item === option));
    state.variantId = option.dataset.variantId;
    state.baseCents = Number(option.dataset.priceCents || 0);
    state.compareCents = Number(option.dataset.compareCents || 0);
    state.available = option.dataset.available === 'true';

    if (state.variantInput) {
      state.variantInput.value = state.variantId;
      state.variantInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    updateDisplayedPrice(state);
    state.actionButtons.forEach((button) => {
      button.disabled = !state.available;
      const label = button.querySelector('span');
      if (label) label.textContent = state.available ? button.dataset.availableText : button.dataset.soldOutText;
    });

    if (option.dataset.mediaId) showMedia(state, option.dataset.mediaId);

    // 只改地址栏、不重新请求页面：这样链接可分享，刷新后打开的还是这一档
    const nextUrl = new URL(window.location.href);
    nextUrl.searchParams.set('variant', state.variantId);
    window.history.replaceState({}, '', nextUrl);
  };

  const selectAccessory = (state, option) => {
    state.accessoryOptions.forEach((item) => item.classList.toggle('is-active', item === option));
    state.accessoryId = option.dataset.accessoryVariantId || '';
    state.accessoryCents = Number(option.dataset.accessoryCents || 0);
    updateDisplayedPrice(state);
  };

  const stepQuantity = (state, delta) => {
    const input = state.quantityInput;
    if (!input) return;
    const current = Number(input.value);
    const base = Number.isFinite(current) && current > 0 ? current : 1;
    input.value = Math.max(1, base + delta);
  };

  const setError = (state, message) => {
    if (!state.errorEl) return;
    state.errorEl.textContent = message || '';
    state.errorEl.hidden = !message;
  };

  const addItems = async (state) => {
    const quantity = Math.max(1, Math.round(Number(state.quantityInput?.value) || 1));
    const items = [{ id: Number(state.variantId), quantity }];
    if (state.accessoryId) items.push({ id: Number(state.accessoryId), quantity: 1 });

    const body = { items };
    const surface = findCartSurface();
    if (surface) {
      body.sections = surface.getSectionsToRender().map((entry) => entry.id);
      body.sections_url = window.location.pathname;
      surface.setActiveElement(document.activeElement);
    }

    const response = await fetch(`${window.routes.cart_add_url}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    const payload = await response.json().catch(() => null);

    if (!response.ok) {
      throw new Error(payload?.description || payload?.message || window.cartStrings.error);
    }
    return { payload, surface };
  };

  // 两个按钮共用「在途」判断：连点两下会加两份，所以按钮先锁上再发请求
  const withButtonLock = async (state, button, run) => {
    if (state.busy) return;
    state.busy = true;
    button.disabled = true;
    setError(state, '');

    try {
      await run();
    } catch (error) {
      setError(state, error.message || window.cartStrings.error);
      console.error(error);
    } finally {
      state.busy = false;
      button.disabled = !state.available;
    }
  };

  const onAddToCart = (state, button) =>
    withButtonLock(state, button, async () => {
      const { payload, surface } = await addItems(state);

      if (!surface) {
        window.location.href = window.routes.cart_url;
        return;
      }
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'starole-product', cartData: payload });
      surface.renderContents(payload);
    });

  const onBuyNow = (state, button) =>
    withButtonLock(state, button, async () => {
      await addItems(state);
      window.location.href = '/checkout';
    });

  // 与 initProduct 对称。state.root 会钉住被替换掉的旧 DOM，WeakMap 回收不掉，
  // 预热那个 IntersectionObserver 还额外攥着旧 root —— 编辑器每次改设置就多留一份。
  const teardownProduct = (root) => {
    productStates.get(root)?.stopWarmup?.();
    productStates.delete(root);
  };

  const initProduct = (root) => {
    if (productStates.has(root)) return;

    const state = {
      root,
      mediaItems: [...root.querySelectorAll('[data-product-image]')],
      thumbnails: [...root.querySelectorAll('[data-product-thumbnail]')],
      tierOptions: [...root.querySelectorAll('[data-tier-option]')],
      accessoryOptions: [...root.querySelectorAll('[data-accessory-option]')],
      actionButtons: [...root.querySelectorAll('[data-product-action]')],
      track: root.querySelector('[data-media-track]'),
      prevButton: root.querySelector('[data-media-prev]'),
      nextButton: root.querySelector('[data-media-next]'),
      variantInput: root.querySelector('[data-selected-variant]'),
      priceEl: root.querySelector('[data-detail-price]'),
      compareEl: root.querySelector('[data-detail-compare]'),
      quantityInput: root.querySelector('[data-product-quantity]'),
      errorEl: root.querySelector('[data-product-error]'),
      currency: root.dataset.currency || 'USD',
      locale: root.dataset.locale || 'en',
      variantId: root.querySelector('[data-selected-variant]')?.value || '',
      baseCents: 0,
      compareCents: 0,
      accessoryId: '',
      accessoryCents: 0,
      available: true,
      busy: false,
      stopWarmup: null,
    };

    if (!state.tierOptions.length) return;

    const activeOption = state.tierOptions.find((option) => option.classList.contains('is-active'));
    state.baseCents = Number(activeOption?.dataset.priceCents || 0);
    state.compareCents = Number(activeOption?.dataset.compareCents || 0);
    state.available = activeOption?.dataset.available === 'true';

    const activeAccessory = state.accessoryOptions.find((option) => option.classList.contains('is-active'));
    state.accessoryId = activeAccessory?.dataset.accessoryVariantId || '';
    state.accessoryCents = Number(activeAccessory?.dataset.accessoryCents || 0);

    state.stopWarmup = scheduleImageWarmup(root, '[data-product-image] img');

    // 手机端大图横滑：箭头翻页 + 滑动时同步缩略图高亮
    const mediaStep = () => {
      const first = state.mediaItems[0];
      return first ? first.getBoundingClientRect().width : state.track?.clientWidth || 0;
    };
    const goToMedia = (index) => {
      if (!state.track) return;
      const target = Math.max(0, Math.min(state.mediaItems.length - 1, index));
      state.track.scrollTo({ left: target * mediaStep(), behavior: 'smooth' });
    };

    if (state.track) {
      state.track.addEventListener(
        'scroll',
        () => {
          window.clearTimeout(state.scrollTimer);
          state.scrollTimer = window.setTimeout(() => {
            const index = Math.round(state.track.scrollLeft / Math.max(1, mediaStep()));
            state.mediaItems.forEach((media, i) => media.classList.toggle('is-active', i === index));
            state.thumbnails.forEach((thumbnail, i) => thumbnail.classList.toggle('is-active', i === index));
          }, 90);
        },
        { passive: true }
      );
    }

    // 缩略图条：鼠标拖拽横向滚动（触摸交给原生滑动）
    const thumbsRow = root.querySelector('.starole-product__thumbnails');
    if (thumbsRow) {
      let active = false;
      let dragging = false;
      let startX = 0;
      let startLeft = 0;
      let moved = 0;

      thumbsRow.addEventListener('pointerdown', (event) => {
        if (event.pointerType === 'touch') return;
        active = true;
        dragging = false;
        moved = 0;
        startX = event.clientX;
        startLeft = thumbsRow.scrollLeft;
      });

      thumbsRow.addEventListener('pointermove', (event) => {
        if (!active) return;
        const delta = event.clientX - startX;
        if (!dragging && Math.abs(delta) > 5) {
          dragging = true;
          thumbsRow.classList.add('is-dragging');
          thumbsRow.setPointerCapture?.(event.pointerId);
        }
        if (!dragging) return;
        moved = Math.max(moved, Math.abs(delta));
        thumbsRow.scrollLeft = startLeft - delta;
      });

      const endDrag = () => {
        if (!active) return;
        const wasDragging = dragging;
        active = false;
        dragging = false;
        thumbsRow.classList.remove('is-dragging');
        if (wasDragging && moved > 5) {
          const suppressClick = (event) => {
            event.preventDefault();
            event.stopPropagation();
          };
          thumbsRow.addEventListener('click', suppressClick, { capture: true, once: true });
          window.setTimeout(() => {
            thumbsRow.removeEventListener('click', suppressClick, true);
          }, 350);
        }
      };

      thumbsRow.addEventListener('pointerup', endDrag);
      thumbsRow.addEventListener('pointercancel', endDrag);
      thumbsRow.addEventListener('pointerleave', endDrag);

      const thumbStep = () => {
        const first = thumbsRow.querySelector('.starole-product__thumbnail');
        const gap = parseFloat(window.getComputedStyle(thumbsRow).columnGap || '0') || 0;
        return (first ? first.getBoundingClientRect().width : 0) + gap;
      };

      root.querySelector('[data-thumbs-prev]')?.addEventListener('click', () => {
        thumbsRow.scrollBy({ left: -thumbStep(), behavior: 'smooth' });
      });
      root.querySelector('[data-thumbs-next]')?.addEventListener('click', () => {
        thumbsRow.scrollBy({ left: thumbStep(), behavior: 'smooth' });
      });
    }

    state.prevButton?.addEventListener('click', () => {
      goToMedia(Math.round(state.track.scrollLeft / Math.max(1, mediaStep())) - 1);
    });
    state.nextButton?.addEventListener('click', () => {
      goToMedia(Math.round(state.track.scrollLeft / Math.max(1, mediaStep())) + 1);
    });

    // 底部转化条：滚过 buy box 后出现
    const stickyBar = root.querySelector('[data-sticky-buy]');
    if (stickyBar) {
      const stickyPrice = stickyBar.querySelector('[data-sticky-price]');
      const stickyVariant = stickyBar.querySelector('[data-sticky-variant]');
      const stickyQty = stickyBar.querySelector('[data-sticky-qty-input]');
      const mainQty = state.quantityInput;
      const buyForm = root.querySelector('.starole-product__form');
      const toggle = stickyBar.querySelector('[data-sticky-toggle]');

      const setVisible = (visible) => {
        stickyBar.classList.toggle('is-visible', visible);
        stickyBar.setAttribute('aria-hidden', visible ? 'false' : 'true');
        // 转化条可见时把客服气泡顶上去，避免压住 Add to Cart
        document.documentElement.classList.toggle('sticky-buy-visible', visible);
      };

      const onScroll = () => {
        if (!buyForm) return;
        setVisible(buyForm.getBoundingClientRect().bottom < 0);
      };

      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll);
      onScroll();

      // 转化条价格 = 单价 × 数量，并按「2 件 10% / 3 件及以上 15%」的自动折扣折算
      const stickyOff = stickyBar.querySelector('[data-sticky-off]');
      const readQty = () => {
        const raw = (mainQty && mainQty.value) || (stickyQty && stickyQty.value) || '1';
        const n = parseInt(raw, 10);
        return Number.isFinite(n) && n > 0 ? n : 1;
      };
      const renderPrice = () => {
        if (!stickyPrice) return;
        const text = state.priceEl ? state.priceEl.textContent.trim() : '';
        const unit = Number(text.replace(/[^\d.]/g, ''));
        const qty = readQty();
        if (!Number.isFinite(unit) || unit <= 0) {
          stickyPrice.textContent = text.replace(/\s*[A-Z]{3}\s*$/, '');
          return;
        }
        // 折扣比例来自主题设置（section.settings.tier2_discount / tier3_discount）
        const t2 = Number(stickyBar.dataset.tier2Off);
        const t3 = Number(stickyBar.dataset.tier3Off);
        const rate =
          qty >= 3 ? (Number.isFinite(t3) ? t3 : 15) / 100
          : qty === 2 ? (Number.isFinite(t2) ? t2 : 10) / 100
          : 0;
        const total = unit * qty * (1 - rate);
        stickyPrice.textContent =
          '$' +
          total.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        if (stickyOff) {
          stickyOff.textContent = rate ? Math.round(rate * 100) + '% off' : '';
          stickyOff.hidden = !rate;
        }
        stickyPrice.setAttribute(
          'title',
          rate ? qty + ' units \u00b7 ' + Math.round(rate * 100) + '% off applied at checkout' : ''
        );
      };
      if (state.priceEl) {
        const observer = new MutationObserver(renderPrice);
        observer.observe(state.priceEl, { childList: true, characterData: true, subtree: true });
      }
      renderPrice();

      const syncVariant = () => {
        const activeName = root.querySelector('[data-tier-option].is-active .starole-tier-option__name');
        if (activeName && stickyVariant) stickyVariant.textContent = activeName.textContent.trim();
      };
      root.addEventListener('click', (event) => {
        if (event.target.closest('[data-tier-option]')) window.setTimeout(syncVariant, 0);
      });
      syncVariant();

      stickyBar.querySelectorAll('[data-sticky-qty]').forEach((button) => {
        button.addEventListener('click', () => {
          const delta = Number(button.dataset.stickyQty);
          const current = Math.max(1, parseInt(mainQty?.value || '1', 10) || 1);
          const next = Math.max(1, current + delta);
          if (mainQty) mainQty.value = String(next);
          if (stickyQty) stickyQty.value = String(next);
          renderPrice();
        });
      });

      if (mainQty && stickyQty) {
        mainQty.addEventListener('change', () => {
          stickyQty.value = mainQty.value;
          renderPrice();
        });
      }

      toggle?.addEventListener('click', () => {
        const collapsed = stickyBar.classList.toggle('is-collapsed');
        toggle.setAttribute('aria-expanded', collapsed ? 'false' : 'true');
        toggle.setAttribute(
          'aria-label',
          collapsed ? 'Expand product bar' : 'Collapse product bar'
        );
      });
    }

    productStates.set(root, state);
  };

  const initAll = (scope = document) => {
    scope.querySelectorAll('[data-starole-product]').forEach(initProduct);
  };

  document.addEventListener('click', (event) => {
    const root = event.target.closest('[data-starole-product]');
    if (!root) return;
    const state = productStates.get(root);
    if (!state) return;

    const thumbnail = event.target.closest('[data-product-thumbnail]');
    if (thumbnail) {
      showMedia(state, thumbnail.dataset.mediaId);
      if (state.track) {
        const index = state.thumbnails.indexOf(thumbnail);
        const width = state.mediaItems[0]?.getBoundingClientRect().width || state.track.clientWidth;
        state.track.scrollTo({ left: index * width, behavior: 'smooth' });
      }
      return;
    }

    const tierOption = event.target.closest('[data-tier-option]');
    if (tierOption) {
      selectVariant(state, tierOption);
      return;
    }

    const accessoryOption = event.target.closest('[data-accessory-option]');
    if (accessoryOption) {
      selectAccessory(state, accessoryOption);
      return;
    }

    if (event.target.closest('[data-quantity-minus]')) {
      stepQuantity(state, -1);
      return;
    }

    if (event.target.closest('[data-quantity-plus]')) {
      stepQuantity(state, 1);
      return;
    }

    const buyButton = event.target.closest('[data-detail-buy]');
    if (buyButton) {
      onBuyNow(state, buyButton);
      return;
    }

    const addButton = event.target.closest('[data-detail-add]');
    if (addButton) onAddToCart(state, addButton);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(document));
  } else {
    initAll(document);
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));

  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('[data-starole-product]').forEach(teardownProduct);
  });
})();


  // Judge.me 评论轮播：等 widget 渲染后注入左右箭头，点击时触发应用自带箭头
  (() => {
    const initReviewArrows = () => {
      const carousel = document.querySelector('.jdgm-cards-carousel');
      if (!carousel || carousel.dataset.staroleArrows === '1') return Boolean(carousel);
      const content = carousel.querySelector('.jdgm-content');
      if (!content) return false;
      const appArrows = carousel.querySelectorAll('.jdgm-arrow');
      if (appArrows.length < 2) return false;

      const makeButton = (direction) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = `starole-jdgm-nav starole-jdgm-nav--${direction}`;
        button.setAttribute('aria-label', direction === 'prev' ? 'Previous review' : 'Next review');
        button.textContent = direction === 'prev' ? '\u2039' : '\u203a';
        button.addEventListener('click', () => {
          const arrows = carousel.querySelectorAll('.jdgm-arrow');
          const target = direction === 'prev' ? arrows[0] : arrows[arrows.length - 1];
          target?.click();
        });
        return button;
      };

      content.append(makeButton('prev'), makeButton('next'));
      carousel.dataset.staroleArrows = '1';
      return true;
    };

    if (!initReviewArrows()) {
      let tries = 0;
      const timer = window.setInterval(() => {
        tries += 1;
        if (initReviewArrows() || tries > 40) window.clearInterval(timer);
      }, 500);
    }
  })();
